import { ObjectsDrawer } from './ObjectsDrawer';
import { AspObject, ObjectType } from './AspObject';
import { DisplayObject } from './DisplayObject';
import { IMapMngr, IObjectsDrawer, IObjectsPool } from './interfaces';
import { ObjectsPool } from './ObjectsPool';
import { aspMngr } from './AspMngr';
import * as utils from '../common/utils';
import { ReturnStatement } from '@angular/compiler';
import * as settings from '../common/settings';
import { Consts } from '@core_n/coordinates/Consts';


const DRAWING_LINE_WIDTH = 3;

export const TEMP_SYMBOL_MARKER = 'm';
export const TEMP_SYMBOL_ARROW = 'a';

export enum TempDrawingDataType {
  Geometry = 'temp-drawing-geom',
  Symbol = 'temp-draing-symbol',
  Text = 'temp-drawing-text'
}

//
// Inner classes
//

const miscSymbolNames = [
  'marker',
];

const miscSymbolSvg = [
  'Pending',
]

const imagesToLoad = ObjectsDrawer.initImagesToLoad(miscSymbolSvg, undefined, miscSymbolNames);

//
// Temporary drawing object
//
export class TempDrawingObject extends AspObject {

  constructor(objectsPool: IObjectsPool, public dataType: TempDrawingDataType, geometry: any, public color: string, public value: string,
              public alignment = { horizontal: 'center', vertical: 'center' }) {
    super(objectsPool, objectsPool.getObjectType(), AspObject.generateId(objectsPool.getObjectType()));
    this.geometry = geometry;
  }
  
  updateGeometry(geometry: any) {
    this.geometry = geometry;
    this.notifyDataChange();
  }

  getColor(): number[] {
    return utils.cssColorToNewmi(this.color);
  }
  
  getTitle(): string { return null; }
  
  handleMsg(): boolean { return false; }
}

//
// Temporary drawings pool
//
export class TempDrawingsPool extends ObjectsPool {

  private static _self: TempDrawingsPool = null;

  public static getSelf(): TempDrawingsPool {
    if (this._self == null)
      this._self = new TempDrawingsPool();
    return this._self;
  }

  private constructor() {
    super(ObjectType.TempDrawing, -1);
    aspMngr.addObjectsPool(this);
  }

  deleteAllObjects() {
    // do not delete all drawings automatically
  }

  // create temporary drawing object
  createTempDrawing(dataType: TempDrawingDataType, geometry: any,
    data: { color?: string, value?: string, alignment?: any }): TempDrawingObject {
    let obj = new TempDrawingObject(this, dataType, geometry, data.color, data.value, data.alignment ? data.alignment : undefined);
    this.addObject(obj);
    return obj;
  }

  // delete temporary drawing object
  deleteTempDrawing(obj: TempDrawingObject) {
    this.deleteObject(obj);
  }

  handleMsg(): void { }
}

//
// Temporary drawing display object
//
class TempDrawingDisplayObject extends DisplayObject {

  constructor(aspObject: AspObject, drawer: IObjectsDrawer) {
    super(aspObject, drawer);

    let tempDrawObject = aspObject as TempDrawingObject;
    
    if (tempDrawObject.dataType == TempDrawingDataType.Geometry)
      this.drawData[tempDrawObject.dataType] = this.drawData[tempDrawObject.objectType];
    else if (tempDrawObject.dataType == TempDrawingDataType.Text || tempDrawObject.dataType == TempDrawingDataType.Symbol) {
      this.drawData[tempDrawObject.dataType] = new NeWMI.Object.NeWMIObjectDM.TextOrSymbolDrawData();
      this.drawData[tempDrawObject.dataType].alignment = tempDrawObject.alignment;
    }

    this.onDataChange();

    this.drawer.addObjectToDm(this, [tempDrawObject.dataType], [tempDrawObject.dataType]);
    this.drawer.getMapMngr().redrawMap();

    this.isDisplayed = true;
  }

  updateDrawValues(): void {
    let tempDrawObject = this.aspObject as TempDrawingObject;
    let color = tempDrawObject.getColor();

    if (tempDrawObject.dataType == TempDrawingDataType.Geometry) {
      this.drawData[tempDrawObject.dataType].geometry = this.geometry;
      this.drawData[tempDrawObject.dataType].line = { width: DRAWING_LINE_WIDTH, color: color };
    }
    else if (tempDrawObject.dataType == TempDrawingDataType.Text || tempDrawObject.dataType == TempDrawingDataType.Symbol) {
      this.drawData[tempDrawObject.dataType].location = this.geometry;
      this.drawData[tempDrawObject.dataType].value = tempDrawObject.value;
      this.drawData[tempDrawObject.dataType].color = tempDrawObject.dataType == TempDrawingDataType.Text ? color : [1, 1, 1];
    }
  }

  onDataChange() {
    super.onDataChange();
    if (this.isDisplayed) {
      this.drawer.updateObjectInDm(this);
      this.drawer.getMapMngr().redrawMap();
    }
  }

}

//
// Temporary drawings drawer
//
export class TempDrawingsDrawer extends ObjectsDrawer {

  constructor(mapMngr: IMapMngr, parentLayer) {
    super(mapMngr, TempDrawingsPool.getSelf(), null, parentLayer, null, imagesToLoad, null, null);

    this.dataKeys.push(TempDrawingDataType.Geometry, TempDrawingDataType.Text, TempDrawingDataType.Symbol);
    this.typeKeys.push(TempDrawingDataType.Geometry, TempDrawingDataType.Text, TempDrawingDataType.Symbol);
  }

  protected addDrawTypes(images) {
    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.Geometry, TempDrawingDataType.Geometry,
      {
        line: { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor }
      });
    
    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.TextOrSymbol, TempDrawingDataType.Text,
      {
        style: this.normalTextStyle,
        properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EORotation | NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor,
      });

    let arrImages = [];
    arrImages[TEMP_SYMBOL_MARKER] = images['marker'];
    arrImages[TEMP_SYMBOL_ARROW] = images['Pending'];
    let symbolStyle = new NeWMI.Draw.Styles.ImagesFontStyle({ images: arrImages, isSymbol: true });
    images['Pending'].size = { width: 25, height: 25};
    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.TextOrSymbol, TempDrawingDataType.Symbol,
      {
        style: symbolStyle,
        properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EORotation | NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor,
      });
  }

  createDisplayObject(obj: AspObject): DisplayObject {
    const dispObj = new TempDrawingDisplayObject(obj, this);
    return dispObj;
  }

  onObjectDelete(obj: AspObject): void {
    // notified of temporary drawing object deletion
    super.onObjectDelete(obj);
    this.mapMngr.redrawMap();
  }

  periodicUpdate(): boolean {
    // this.drawMgr.typesVisualProps[this.objectsType].globalParams.factor = settings.userSettings.symbolsSize;
    return super.periodicUpdate();
  }

}
